import * as Api from '@ViewModels';
import momentTz from 'moment-timezone';

export const DAY_OF_WEEK_NAMES: readonly Api.DayOfWeekName[] = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;
const SCHEDULE_TIME_STEP_MINUTES = 30;

export interface LocalSchedule {
	dayIndexes: number[];
	minuteOfDay: number;
}

export interface ScheduleTimeOption {
	minuteOfDay: number;
	label: string;
}

const SCHEDULE_ELIGIBLE_PROVIDERS: ReadonlySet<string> = new Set([
	'Google',
	'Gmail',
	'Outlook',
	'Office365',
	'Yahoo',
	'Aol',
	'Internal',
]);

export const canScheduleEmailExec = (user: Partial<Api.IUser> | null | undefined): boolean =>
	!!user?.emailProvider && SCHEDULE_ELIGIBLE_PROVIDERS.has(user.emailProvider);

export const getScheduleTimezone = (user: Partial<Api.IUser> | null | undefined): string => {
	const preferred = user?.userPreferences?.timeZone;
	return preferred && momentTz.tz.zone(preferred) ? preferred : momentTz.tz.guess();
};

export const getUtcOffsetMinutes = (timeZone: string): number => momentTz.tz(timeZone).utcOffset();

export const getTimezoneLabel = (timeZone: string): string => {
	const abbreviation = momentTz.tz(timeZone).zoneAbbr();
	return abbreviation && !/^[+-]/.test(abbreviation) ? abbreviation : timeZone.replace(/_/g, ' ');
};

const normalizeWeekMinute = (weekMinute: number): number =>
	((weekMinute % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) % MINUTES_PER_WEEK;

export const formatTimeLabel = (minuteOfDay: number): string => {
	const hour = Math.floor(minuteOfDay / 60) % 24;
	const minute = minuteOfDay % 60;
	const period = hour < 12 ? 'AM' : 'PM';
	const hour12 = hour % 12 === 0 ? 12 : hour % 12;
	return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
};

export const getScheduleTimeOptions = (offsetMinutes: number): ScheduleTimeOption[] => {
	const options: ScheduleTimeOption[] = [];
	for (let utcMinute = 0; utcMinute < MINUTES_PER_DAY; utcMinute += SCHEDULE_TIME_STEP_MINUTES) {
		const local = (((utcMinute + offsetMinutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
		options.push({ minuteOfDay: local, label: formatTimeLabel(local) });
	}
	return options.sort((a, b) => a.minuteOfDay - b.minuteOfDay);
};

export const localScheduleToUtcSlots = (schedule: LocalSchedule, offsetMinutes: number): Api.IWeeklyScheduleSlot[] =>
	[...schedule.dayIndexes]
		.sort((a, b) => a - b)
		.map(dayIndex => {
			const utcWeekMinute = normalizeWeekMinute(dayIndex * MINUTES_PER_DAY + schedule.minuteOfDay - offsetMinutes);
			return {
				dayOfWeek: DAY_OF_WEEK_NAMES[Math.floor(utcWeekMinute / MINUTES_PER_DAY)],
				hour: Math.floor((utcWeekMinute % MINUTES_PER_DAY) / 60),
				minute: utcWeekMinute % 60,
			};
		});

export const utcSlotsToLocalSchedule = (
	slots: Api.IWeeklyScheduleSlot[] | null | undefined,
	offsetMinutes: number
): LocalSchedule | null => {
	if (!slots?.length) {
		return null;
	}
	const localized = slots.map(slot => {
		const dayIndex = DAY_OF_WEEK_NAMES.indexOf(slot.dayOfWeek);
		const localWeekMinute = normalizeWeekMinute(
			dayIndex * MINUTES_PER_DAY + slot.hour * 60 + slot.minute + offsetMinutes
		);
		return { dayIndex: Math.floor(localWeekMinute / MINUTES_PER_DAY), minuteOfDay: localWeekMinute % MINUTES_PER_DAY };
	});
	const countsByMinute = new Map<number, number>();
	for (const { minuteOfDay } of localized) {
		countsByMinute.set(minuteOfDay, (countsByMinute.get(minuteOfDay) ?? 0) + 1);
	}
	const [minuteOfDay] = [...countsByMinute.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
	const dayIndexes = [...new Set(localized.map(entry => entry.dayIndex))].sort((a, b) => a - b);
	return { dayIndexes, minuteOfDay };
};

export const DEFAULT_SCHEDULE_DAY_INDEXES: readonly number[] = [1, 2, 3, 4, 5];
const DEFAULT_SCHEDULE_MINUTE_OF_DAY = 8 * 60;

export const getDefaultScheduleMinute = (options: ScheduleTimeOption[]): number => {
	let best = options[0]?.minuteOfDay ?? DEFAULT_SCHEDULE_MINUTE_OF_DAY;
	for (const option of options) {
		if (
			Math.abs(option.minuteOfDay - DEFAULT_SCHEDULE_MINUTE_OF_DAY) < Math.abs(best - DEFAULT_SCHEDULE_MINUTE_OF_DAY)
		) {
			best = option.minuteOfDay;
		}
	}
	return best;
};
